import { useCallback, useMemo } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import type { FileExplorerSearchParams } from "@/features/categories/types";
import type { JobPostingQueryRequest } from "@/services/api/job-postings";

/**
 * Hook for job posting search params within the file-explorer route.
 * Uses jp-prefixed search params to avoid collision with other
 * tab views sharing the same file explorer route.
 */
export function useJobPostingSearchParams() {
  const search = useSearch({
    from: "/file-explorer/$categoryName",
  }) as FileExplorerSearchParams;
  const navigate = useNavigate({ from: "/file-explorer/$categoryName" });

  const pagination = useMemo<PaginationState>(() => {
    const page = search.jpPage ? Number(search.jpPage) : 1;
    const pageSize = search.jpPageSize ? Number(search.jpPageSize) : 30;
    return {
      pageIndex: Math.max(0, page - 1),
      pageSize: Math.max(1, pageSize),
    };
  }, [search.jpPage, search.jpPageSize]);

  const sorting = useMemo<SortingState>(() => {
    if (!search.jpSortBy) return [];
    const desc = search.jpSortDirection === "DESC";
    return [{ id: String(search.jpSortBy), desc }];
  }, [search.jpSortBy, search.jpSortDirection]);

  const filters = useMemo<Partial<JobPostingQueryRequest>>(() => {
    const parsed: Partial<JobPostingQueryRequest> = {};

    if (search.jpSourceName) parsed.sourceName = String(search.jpSourceName);
    if (search.jpLocation) parsed.location = String(search.jpLocation);
    if (search.jpCompany) parsed.company = String(search.jpCompany);
    if (search.jpSearchTerm) {
      const terms = String(search.jpSearchTerm).split("||").filter(Boolean);
      if (terms.length > 0) parsed.searchTerm = terms;
    }
    if (search.jpIsActive !== undefined && search.jpIsActive !== "") {
      parsed.isActive = search.jpIsActive === "true";
    }
    if (search.jpSearchFields) {
      const fields = String(search.jpSearchFields)
        .split(",")
        .filter((f) => ["Title", "Company", "Location", "Description"].includes(f));
      if (fields.length > 0 && fields.length < 4) {
        parsed.searchFields = fields as JobPostingQueryRequest["searchFields"];
      }
    }

    return parsed;
  }, [
    search.jpSourceName,
    search.jpLocation,
    search.jpCompany,
    search.jpSearchTerm,
    search.jpIsActive,
    search.jpSearchFields,
  ]);

  const queryRequest = useMemo<JobPostingQueryRequest>(() => {
    const sortField =
      sorting.length > 0 ? sorting[0].id : "lastSeenAt";
    const sortDir =
      sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : "DESC";

    return {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sortField,
      sortDirection: sortDir,
      ...filters,
    };
  }, [pagination, sorting, filters]);

  const setPagination = useCallback(
    (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      const newPagination =
        typeof updater === "function" ? updater(pagination) : updater;
      const page = newPagination.pageIndex + 1;

      const newSearch: FileExplorerSearchParams = { ...search };
      newSearch.jpPage = page > 1 ? String(page) : undefined;
      newSearch.jpPageSize =
        newPagination.pageSize !== 30
          ? String(newPagination.pageSize)
          : undefined;

      navigate({
        search: newSearch as Partial<
          Record<keyof FileExplorerSearchParams, string>
        >,
        replace: true,
      });
    },
    [pagination, search, navigate]
  );

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;

      const newSearch: FileExplorerSearchParams = { ...search };
      newSearch.jpSortBy = newSorting.length > 0 ? newSorting[0].id : undefined;
      newSearch.jpSortDirection =
        newSorting.length > 0
          ? newSorting[0].desc
            ? "DESC"
            : "ASC"
          : undefined;
      newSearch.jpPage = undefined;

      navigate({
        search: newSearch as Partial<
          Record<keyof FileExplorerSearchParams, string>
        >,
        replace: true,
      });
    },
    [sorting, search, navigate]
  );

  const setFilters = useCallback(
    (newFilters: Partial<JobPostingQueryRequest>) => {
      const newSearch: FileExplorerSearchParams = { ...search };

      delete newSearch.jpSourceName;
      delete newSearch.jpLocation;
      delete newSearch.jpCompany;
      delete newSearch.jpSearchTerm;
      delete newSearch.jpIsActive;
      delete newSearch.jpSearchFields;
      delete newSearch.jpPage;

      if (newFilters.sourceName) newSearch.jpSourceName = newFilters.sourceName;
      if (newFilters.location) newSearch.jpLocation = newFilters.location;
      if (newFilters.company) newSearch.jpCompany = newFilters.company;
      if (newFilters.searchTerm && newFilters.searchTerm.length > 0) {
        newSearch.jpSearchTerm = newFilters.searchTerm.join("||");
      }
      if (newFilters.isActive !== undefined && newFilters.isActive !== null) {
        newSearch.jpIsActive = String(newFilters.isActive);
      }
      if (newFilters.searchFields && newFilters.searchFields.length > 0 && newFilters.searchFields.length < 4) {
        newSearch.jpSearchFields = newFilters.searchFields.join(",");
      }

      navigate({
        search: newSearch as Partial<
          Record<keyof FileExplorerSearchParams, string>
        >,
        replace: true,
      });
    },
    [search, navigate]
  );

  return {
    pagination,
    sorting,
    filters,
    queryRequest,
    setPagination,
    setSorting,
    setFilters,
  };
}
